import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'
import surveyCompleteBadge from '../../assets/survey-complete-badge.png'
import { CelebrationAnimation } from '@/shared/components/CelebrationAnimation'
import { celebrationAssets } from '@/shared/lib/celebrationAssets'
import {
  SURVEY_ALREADY_SUBMITTED_SUBTITLE,
  SURVEY_ALREADY_SUBMITTED_TITLE,
  SIGNUP_SURVEY_COMPLETE_REWARD_BODY,
  SIGNUP_SURVEY_COMPLETE_SUBTITLE,
  SURVEY_COMPLETE_NO_REWARD_THANK_YOU,
  SURVEY_COMPLETE_REWARD_BODY,
  SURVEY_COMPLETE_THANK_YOU,
  SURVEY_COMPLETE_TITLE,
  WEB_SURVEY_COMPLETE_CONGRATS,
  WEB_SURVEY_COMPLETE_REWARD_BODY,
  WEB_SURVEY_COMPLETE_JOURNEY_TITLE,
  WEB_SURVEY_COMPLETE_PARTICIPATION,
  surveyCompleteEarnedHeading,
  webSurveyCompleteEarnedLine,
} from '../../lib/survey-completion-messages'
import { completionScreenHasReward } from '../../lib/survey-completion-reward'
import { compactRewardLabel } from '../../lib/signup-survey-welcome-copy'
import type { ProviderReward } from '../../lib/types'
import type { SurveyCompletionVariant } from '../../lib/survey-completion-variant'
import {
  surveyCompletionBadgeImgSx,
  surveyCompletionCardBodySx,
  surveyCompletionCardSx,
  surveyCompletionCelebrationImgSx,
  surveyCompletionIllustrationSx,
  surveyCompletionPageWrapSx,
  surveyCompletionRewardBodyGuestSx,
  surveyCompletionRewardBodySx,
  surveyCompletionRewardCardSx,
  surveyCompletionRewardEarnedSx,
  surveyCompletionRewardHeadingSx,
  surveyCompletionRewardTextSx,
  surveyCompletionRootSx,
  surveyCompletionSubtitleSx,
  surveyCompletionTitleSx,
} from '../../styles/surveyExecutionStyles'
import { SurveySignupStepper } from '../SurveySignupStepper'
import { SurveyCompletionActions } from './SurveyCompletionActions'

export type { SurveyCompletionVariant }

interface SurveyCompletionViewProps {
  readonly reward: ProviderReward
  /** Instrument type from info API — no-reward instruments never show reward UI (TC-SPW-56). */
  readonly instrumentRewardEnabled: boolean
  /** Fresh = just submitted; alreadySubmitted = return visit (no reward card or Claim Now). */
  readonly variant?: SurveyCompletionVariant
  readonly onClaimNow: () => void
  readonly onHome: () => void
  /** BFP-1025 — Start eKYC / Remind Me Later instead of Claim Now. */
  readonly signupSurveyMode?: boolean
  readonly signupSurveyEkycGated?: boolean
  readonly onStartEkyc?: () => void
  readonly onRemindLater?: () => void
  /** BFP-1120 — guest SMS web survey completion. */
  readonly webSurveySignupMode?: boolean
  readonly surveyFirstJourney?: boolean
  readonly onCreateAccount?: () => void
}

function rewardCardBody(opts: {
  readonly webSurveySignupMode: boolean
  readonly signupSurveyMode: boolean
}): string {
  if (opts.webSurveySignupMode) {
    return WEB_SURVEY_COMPLETE_REWARD_BODY
  }
  if (opts.signupSurveyMode) {
    return SIGNUP_SURVEY_COMPLETE_REWARD_BODY
  }
  return SURVEY_COMPLETE_REWARD_BODY
}

function rewardCardHeading(opts: {
  readonly webSurveySignupMode: boolean
  readonly display: string | null
}): ReactNode {
  if (opts.webSurveySignupMode) {
    if (!opts.display) return WEB_SURVEY_COMPLETE_CONGRATS
    return (
      <>
        {WEB_SURVEY_COMPLETE_CONGRATS}
        <Box component="span" sx={surveyCompletionRewardEarnedSx}>
          {webSurveyCompleteEarnedLine(opts.display)}
        </Box>
      </>
    )
  }
  if (opts.display) return surveyCompleteEarnedHeading(opts.display)
  return WEB_SURVEY_COMPLETE_CONGRATS
}

/** Figma 5076:7406 / 5074-13982 — post-submit completion screen. */
export function SurveyCompletionView({
  reward,
  instrumentRewardEnabled,
  variant = 'fresh',
  onClaimNow,
  onHome,
  signupSurveyMode = false,
  signupSurveyEkycGated = true,
  onStartEkyc,
  onRemindLater,
  webSurveySignupMode = false,
  surveyFirstJourney = false,
  onCreateAccount,
}: SurveyCompletionViewProps) {
  const isAlreadySubmitted = variant === 'alreadySubmitted'
  const hasReward =
    !isAlreadySubmitted &&
    instrumentRewardEnabled &&
    completionScreenHasReward(reward)
  const rewardAmountLabel = compactRewardLabel(reward) ?? reward.display
  const showWebSurveyRewardCta = Boolean(webSurveySignupMode) && !isAlreadySubmitted
  const title = isAlreadySubmitted
    ? SURVEY_ALREADY_SUBMITTED_TITLE
    : SURVEY_COMPLETE_TITLE
  let subtitle = SURVEY_COMPLETE_NO_REWARD_THANK_YOU
  if (isAlreadySubmitted) {
    subtitle = SURVEY_ALREADY_SUBMITTED_SUBTITLE
  } else if (hasReward || showWebSurveyRewardCta) {
    subtitle = surveyFirstJourney
      ? WEB_SURVEY_COMPLETE_PARTICIPATION
      : signupSurveyMode
        ? SIGNUP_SURVEY_COMPLETE_SUBTITLE
        : SURVEY_COMPLETE_THANK_YOU
  }

  return (
    <Box
      data-testid="survey-completion-view"
      data-survey-completion-variant={variant}
      data-signup-survey-mode={signupSurveyMode ? 'true' : 'false'}
      sx={surveyCompletionPageWrapSx}
    >
      <Box sx={surveyCompletionRootSx}>
        <Box sx={surveyCompletionCardSx}>
          <Box sx={surveyCompletionIllustrationSx} aria-hidden data-testid="survey-completion-celebration">
            <CelebrationAnimation
              gifSrc={celebrationAssets.surveyCompletedGif}
              staticFallbackSrc={celebrationAssets.surveyCompletedStaticFallback}
              playing
              sx={surveyCompletionCelebrationImgSx}
              data-testid="survey-completion-animation"
            />
          </Box>

          <Box sx={surveyCompletionCardBodySx}>
              <Typography component="h1" sx={surveyCompletionTitleSx}>
                {title}
              </Typography>
              <Typography component="p" sx={surveyCompletionSubtitleSx}>
                {subtitle}
              </Typography>

              {(hasReward && reward.display) || showWebSurveyRewardCta ? (
                <Box sx={surveyCompletionRewardCardSx} data-testid="survey-completion-reward-card">
                  <Box sx={surveyCompletionRewardTextSx}>
                    <Typography component="h2" sx={surveyCompletionRewardHeadingSx}>
                      {rewardCardHeading({
                        webSurveySignupMode,
                        display: hasReward ? rewardAmountLabel : null,
                      })}
                    </Typography>
                    <Typography
                      component="p"
                      sx={
                        webSurveySignupMode
                          ? surveyCompletionRewardBodyGuestSx
                          : surveyCompletionRewardBodySx
                      }
                    >
                      {rewardCardBody({
                        webSurveySignupMode,
                        signupSurveyMode,
                      })}
                    </Typography>
                  </Box>
                  <Box
                    component="img"
                    src={surveyCompleteBadge}
                    alt=""
                    sx={surveyCompletionBadgeImgSx}
                  />
                </Box>
              ) : null}

              {surveyFirstJourney ? (
                <Box sx={{ width: '100%', mt: 3 }} data-testid="survey-completion-journey">
                  <Typography
                    component="h2"
                    sx={{
                      ...surveyCompletionTitleSx,
                      fontSize: 24,
                      lineHeight: '40px',
                      mb: 3,
                    }}
                  >
                    {WEB_SURVEY_COMPLETE_JOURNEY_TITLE}
                  </Typography>
                  <SurveySignupStepper activeStep={1} />
                </Box>
              ) : null}

              <SurveyCompletionActions
                hasReward={hasReward}
                signupSurveyMode={signupSurveyMode}
                signupSurveyEkycGated={signupSurveyEkycGated}
                webSurveySignupMode={webSurveySignupMode}
                surveyFirstJourney={surveyFirstJourney}
                onClaimNow={onClaimNow}
                onHome={onHome}
                onStartEkyc={onStartEkyc}
                onRemindLater={onRemindLater}
                onCreateAccount={onCreateAccount}
              />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
